import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut } from "firebase/auth";
import { auth, api } from "./firebase";
import { Y, T, LABEL_FONT, BG_SRC, setThemeMode, setThemeOverride } from "./theme";
import { useTheme } from "./useTheme";
import { IcoGenerate, IcoLibrary, IcoCredits, IcoSettings } from "./icons";
import AdminView from "./components/AdminView";
import { Logo, LeopardBg } from "./components/shared";
import LoginScreen from "./components/LoginScreen";
import DiscordCallback from "./components/DiscordCallback";

import GenerateView from "./components/GenerateView";
import LibraryView from "./components/LibraryView";
import DashboardView from "./components/DashboardView";
import BankView from "./components/BankView";
import PurchaseView from "./components/PurchaseView";
import SettingsView from "./components/SettingsView";

// ── Header-right context (pages inject content into the right side of header)
const HeaderRightCtx = createContext();
export function useHeaderRight() { return useContext(HeaderRightCtx); }

// ── Scroll-to-top context ────────────────────────────────────────────────────
// The Studio shell scrolls inside a `position:fixed` overflow:auto div, not
// the document - so window.scrollTo() is a no-op. Pages can call
// useScrollToTop()() to scroll that real scroll container back to 0.
const ScrollToTopCtx = createContext(() => {});
export function useScrollToTop() { return useContext(ScrollToTopCtx); }

// ── Profile-refresh context (lets settings tell App to re-pull avatar/account)
const ProfileCtx = createContext(() => Promise.resolve());
export function useRefreshProfile() { return useContext(ProfileCtx); }

// ── Loading-lock context ─────────────────────────────────────────────────────
// Lets any descendant component declaratively block the PageLoader on its
// route from revealing until it's done loading. The PageLoader watches
// `pending` and only fires its reveal phase when it hits 0. Each call to
// useLoadingLock(true) registers a lock; flipping to false (or unmount)
// resolves it. AuthImage / AuthVideo and other async media components
// register here so the form panel doesn't reveal mid-load.
const LoadingLockCtx = createContext({ register: () => {}, resolve: () => {}, pending: 0 });
export function useLoadingLockState() { return useContext(LoadingLockCtx); }

function LoadingLockProvider({ children }) {
  const [pending, setPending] = useState(0);
  const setRef = useRef(null);
  if (setRef.current === null) setRef.current = new Set();
  const register = useCallback((id) => {
    setRef.current.add(id);
    setPending(setRef.current.size);
  }, []);
  const resolve = useCallback((id) => {
    if (setRef.current.delete(id)) setPending(setRef.current.size);
  }, []);
  return <LoadingLockCtx.Provider value={{ register, resolve, pending }}>{children}</LoadingLockCtx.Provider>;
}

// Register a lock while `active` is true. Resolves on unmount or when
// `active` flips false. Safe to call inside components that may be
// rendered outside a provider — the default ctx is a no-op.
export function useLoadingLock(active) {
  const ctx = useContext(LoadingLockCtx);
  useEffect(() => {
    if (!active) return;
    const id = Symbol('loading-lock');
    ctx.register(id);
    return () => ctx.resolve(id);
  }, [active, ctx]);
}

// ── Notification context ─────────────────────────────────────────────────────
const NotifyCtx = createContext();
export function useNotify() { return useContext(NotifyCtx); }

function NotifyProvider({ children }) {
  // Store the requested duration on the msg itself so each notify call
  // can have its own auto-dismiss timing. NotifyToast handles the
  // actual timer (and its pause-on-hover behavior) since the timer
  // needs to react to hover state.
  const [msg, setMsg] = useState(null);
  const notify = useCallback((type, text, dur = 4000) => {
    setMsg({ type, text, dur, id: Date.now() });
  }, []);
  const clear = useCallback(() => setMsg(null), []);
  return <NotifyCtx.Provider value={{ msg, notify, clear }}>{children}<NotifyToast/></NotifyCtx.Provider>;
}

// Floating toast overlay - replaces the old inline NotifyBadge in the
// header. Renders via createPortal so its stacking context lives at
// the document root and nothing in the route subtree can clip it.
// Auto-dismisses on a timer; hovering pauses the timer (preserves the
// remaining time so a quick mouseover doesn't reset to full duration);
// mouseleave resumes. Close button (X) dismisses early.
function NotifyToast() {
  const { msg, clear } = useNotify();
  const [paused, setPaused] = useState(false);
  const [closing, setClosing] = useState(false);
  const remainingRef = useRef(0);

  // Reset remaining-time when a fresh message lands. Closing flag also
  // resets so the new toast doesn't inherit the previous one's exit
  // animation state.
  useEffect(() => {
    if (!msg) return;
    remainingRef.current = msg.dur;
    setClosing(false);
    setPaused(false);
  }, [msg && msg.id]); // eslint-disable-line

  // Timer: runs while a message is shown and not paused. The cleanup
  // function subtracts elapsed time from the remaining budget so that
  // unpausing later resumes from where we left off rather than starting
  // a fresh full-duration window every time.
  useEffect(() => {
    if (!msg || paused || closing) return;
    if (remainingRef.current <= 0) return;
    const startedAt = Date.now();
    const t = setTimeout(() => {
      // Trigger the fade-out animation; actual clear() fires after the
      // transition end so the toast doesn't pop out abruptly.
      setClosing(true);
    }, remainingRef.current);
    return () => {
      clearTimeout(t);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [msg && msg.id, paused, closing]); // eslint-disable-line

  // Once the exit animation finishes, actually drop the message.
  useEffect(() => {
    if (!closing) return;
    const t = setTimeout(clear, 220);
    return () => clearTimeout(t);
  }, [closing, clear]);

  if (!msg) return null;
  const isErr = msg.type === 'error';
  const accent = isErr ? T.danger : '#44FFAA';
  const accentBg = isErr ? T.dangerBg : 'rgba(68,255,170,0.12)';
  const accentBorder = isErr ? (T.dangerBorder || `${T.danger}55`) : 'rgba(68,255,170,0.35)';

  const node = (
    <div style={{
      position:'fixed',
      top: 'max(20px, env(safe-area-inset-top, 0px))',
      left: 0, right: 0,
      display:'flex', justifyContent:'center',
      zIndex: 10001,
      pointerEvents: 'none', // overlay container is click-through; the
                             // toast itself opts back in below.
    }}>
      <div
        role="status"
        aria-live={isErr ? 'assertive' : 'polite'}
        onMouseEnter={()=>setPaused(true)}
        onMouseLeave={()=>setPaused(false)}
        style={{
          pointerEvents:'auto',
          display:'flex', alignItems:'center', gap:10,
          maxWidth:'min(540px, calc(100% - 24px))',
          padding:'12px 14px 12px 16px',
          background: accentBg,
          color: accent,
          border: `1px solid ${accentBorder}`,
          borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.25)',
          backdropFilter:'blur(8px)',
          WebkitBackdropFilter:'blur(8px)',
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.4,
          opacity: closing ? 0 : 1,
          transform: closing ? 'translateY(-8px)' : 'translateY(0)',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
        }}
      >
        <span style={{display:'inline-flex',flexShrink:0}}>
          {isErr ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          )}
        </span>
        <span style={{flex:1, minWidth:0, whiteSpace:'pre-wrap', wordBreak:'break-word'}}>{msg.text}</span>
        <button
          onClick={()=>setClosing(true)}
          title="Dismiss"
          aria-label="Dismiss notification"
          style={{
            flexShrink:0, marginLeft:4,
            display:'inline-flex', alignItems:'center', justifyContent:'center',
            width:24, height:24, padding:0,
            background:'transparent', color: accent,
            border:'none', borderRadius:6, cursor:'pointer',
            opacity:0.7, transition:'opacity 0.15s',
          }}
          onMouseEnter={e=>e.currentTarget.style.opacity='1'}
          onMouseLeave={e=>e.currentTarget.style.opacity='0.7'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? ReactDOM.createPortal(node, document.body) : node;
}

// ── Confirm dialog system ────────────────────────────────────────────────────
const ConfirmCtx = createContext();
export function useConfirm() { return useContext(ConfirmCtx); }

function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title, message, imageUrl, confirmLabel = "Delete", cancelLabel = "Cancel", danger = false }) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialog({ title, message, imageUrl, confirmLabel, cancelLabel, danger });
    });
  }, []);

  const handleConfirm = () => { resolveRef.current?.(true); setDialog(null); };
  const handleCancel = () => { resolveRef.current?.(false); setDialog(null); };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {dialog && (
        <div style={{position:"fixed",inset:0,zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)"}}
          onClick={handleCancel}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:T.surface, border:`1px solid ${T.border}`, borderRadius:16,
            padding:"28px 32px", maxWidth:380, width:"90%",
            boxShadow:"0 20px 60px rgba(0,0,0,0.5)",
          }}>
            {dialog.title && <div style={{color:T.text,fontSize:16,fontWeight:700,marginBottom:dialog.imageUrl?16:8}}>{dialog.title}</div>}
            {dialog.imageUrl && <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}>
              <div style={{width:120,height:120,borderRadius:4,background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.08)",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <img src={dialog.imageUrl} alt="" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
              </div>
            </div>}
            {dialog.message && <div style={{color:T.text2,fontSize:13,lineHeight:1.5,marginBottom:24}}>{dialog.message}</div>}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={handleCancel} title={dialog.cancelLabel} alt={dialog.cancelLabel} style={{
                padding:"8px 16px",borderRadius:12,background:"rgba(255,255,255,0.06)",border:"none",
                color:T.text2,cursor:"pointer",transition:"background 0.15s",display:"flex",alignItems:"center",justifyContent:"center",
              }} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"} onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
              <button onClick={handleConfirm} title={dialog.confirmLabel} alt={dialog.confirmLabel} style={{
                padding:"8px 16px",borderRadius:12,border:"none",
                background: dialog.danger ? T.danger : Y,
                color: dialog.danger ? "#fff" : T.dark,
                cursor:"pointer",transition:"opacity 0.15s",display:"flex",alignItems:"center",justifyContent:"center",
              }} onMouseEnter={e=>e.currentTarget.style.opacity="0.85"} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

const NAV = [
  { id:"generate",  path:"/generate",         Icon:IcoGenerate,  title:"Generate" },
  { id:"library",   path:"/library",          Icon:IcoLibrary,   title:"Library"  },
  { id:"bank",      path:"/bank",             Icon:IcoCredits,   title:"Bank"     },
  { id:"settings",  path:"/settings",         Icon:IcoSettings,  title:"Settings" },
];

// ── Auth state hook ───────────────────────────────────────────────────────────
function useAuth() {
  const [loggedIn,     setLoggedIn]     = useState(false);
  const [authChecked,  setAuthChecked]  = useState(false);
  const [userPhoto,    setUserPhoto]    = useState(null);
  const [userEmail,    setUserEmail]    = useState(null);
  const [userRole,     setUserRole]     = useState(null);
  const [unreadReqs,   setUnreadReqs]   = useState(0);
  const [signingIn,   setSigningIn]    = useState(false);

  useEffect(() => {
    getRedirectResult(auth).catch((err) => {
      console.error('getRedirectResult error:', err);
      setSigningIn(false);
      setAuthChecked(true);
    });

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserPhoto(user.photoURL || null);
        setUserEmail(user.email);
        try {
          // Force a fresh token to avoid stale/corrupt tokens after popup auth
          await user.getIdToken(true);
          const settings = await api.getSettings();
          setLoggedIn(true);
          setSigningIn(false);
          setUserRole(settings.role || null);
          // Apply server-stored theme preference, but ONLY when this
          // device doesn't already have a localStorage choice. The
          // device-local value is authoritative on reload (otherwise a
          // toggle made via the avatar chip menu, which only writes
          // localStorage, would get overridden by a stale backend
          // value on next reload). For first-time logins / new devices
          // the backend seeds the initial theme.
          let hasLocal = false;
          try { hasLocal = !!localStorage.getItem('katana.theme'); } catch (_) {}
          if (!hasLocal && (settings.theme === 'light' || settings.theme === 'dark')) {
            setThemeMode(settings.theme);
          }
          // Authenticated - drop the dark login override so the user's
          // real theme (applied above / from localStorage) takes effect.
          setThemeOverride(null);
          // Honor the user's chosen avatar source (only meaningful when both
          // Google + Discord are linked; otherwise the linked one wins).
          const discordAvatar = settings.linkedAccounts?.discord?.avatarUrl || null;
          const googleAvatar  = user.photoURL || null;
          const src = settings.avatarSource || (googleAvatar ? 'google' : 'discord');
          setUserPhoto(src === 'discord' ? (discordAvatar || googleAvatar) : (googleAvatar || discordAvatar));
          setAuthChecked(true);
          // Fetch unread request count for admins/owners
          if (settings.role === 'owner' || settings.role === 'admin') {
            api.getUnreadRequestCount().then(d => setUnreadReqs(d.count || 0)).catch(() => {});
          }
        } catch (e) {
          console.error('getSettings failed:', e);
          // Backend rejected token — sign out so user can try again
          await signOut(auth);
          setThemeOverride("dark");
          setLoggedIn(false);
          setSigningIn(false);
          setUserRole(null);
          setAuthChecked(true);
        }
      } else {
        // Signed out -> force the always-dark login screen before React
        // re-renders it, so it never flashes / sticks in light mode.
        setThemeOverride("dark");
        setLoggedIn(false);
        setSigningIn(false);
        setUserEmail(null);
        setUserRole(null);
        setAuthChecked(true);
        if (window.location.pathname !== "/") window.history.replaceState(null, "", "/");
      }
    });
    return () => unsub();
  }, []);

  const login = async () => {
    const provider = new GoogleAuthProvider();
    setSigningIn(true);
    try {
      await signInWithPopup(auth, provider);
    } catch (e) {
      console.error('signInWithPopup failed:', e.code, e.message);
      if (e.code === 'auth/popup-blocked' || e.code === 'auth/popup-closed-by-user') {
        signInWithRedirect(auth, provider);
      } else {
        setSigningIn(false);
      }
    }
  };

  const handleSignOut = () => {
    signOut(auth);
  };

  // Re-fetch settings + recompute the avatar (called by SettingsView after
  // a Discord link/unlink or avatar-source change).
  const refreshProfile = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const settings = await api.getSettings();
      const discordAvatar = settings.linkedAccounts?.discord?.avatarUrl || null;
      const googleAvatar  = user.photoURL || null;
      const src = settings.avatarSource || (googleAvatar ? 'google' : 'discord');
      setUserPhoto(src === 'discord' ? (discordAvatar || googleAvatar) : (googleAvatar || discordAvatar));
    } catch (_) { /* non-fatal */ }
  };

  return { loggedIn, authChecked, signingIn, login, handleSignOut, userPhoto, userEmail, userRole, unreadReqs, setUnreadReqs, refreshProfile };
}

// ── Responsive hook ────────────────────────────────────────────────────────────
export function useNarrow(breakpoint = 800) {
  const [narrow, setNarrow] = useState(window.innerWidth <= breakpoint);
  const fsRef = useRef(false);
  useEffect(() => {
    const onResize = () => {
      if (document.fullscreenElement || document.webkitFullscreenElement) { fsRef.current = true; return; }
      if (fsRef.current) return;
      setNarrow(window.innerWidth <= breakpoint);
    };
    const onFsChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        fsRef.current = true;
        setTimeout(() => { fsRef.current = false; setNarrow(window.innerWidth <= breakpoint); }, 500);
      }
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, [breakpoint]);
  return narrow;
}

// ── Main studio shell (shown when logged in) ──────────────────────────────────
const CONTENT_MAX = 960;

function Studio({ userPhoto, userEmail, userRole, unreadReqs = 0, setUnreadReqs, refreshProfile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [prevPath,  setPrevPath]  = useState(null);
  const [animating, setAnimating] = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [headerRight, setHeaderRight] = useState(null);
  const menuRef = React.useRef(null);
  const scrollerRef = React.useRef(null);
  const NAV_PATHS = NAV.map(n => n.path);
  const narrow = useNarrow();
  // Subscribe to theme so every Studio render reads the current palette.
  // Without this, T.* would still read fresh values but components below
  // wouldn't re-render on toggle - this re-render cascades the whole tree.
  const { mode: themeMode, toggle: toggleTheme } = useTheme();

  // The fixed scroll container persists across nav (so in-flight component
  // state isn't lost), which means its scrollTop carries over from the
  // previous route. Reset it to top whenever the TOP-LEVEL path changes so
  // a new page always starts at the top. We deliberately key off only the
  // first path segment so nested URL changes (e.g. switching the Raster /
  // Vector sub-tab under /generate/image) don't bump the user back to the
  // top mid-form.
  const topLevelPath = location.pathname.split('/')[1] || '';
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [topLevelPath]);

  // Stable callback so descendants can `useScrollToTop()()` whenever they
  // need the page reset (e.g. Reuse button populates the form at the top).
  const scrollToTop = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    try { el.scrollTo({ top: 0, behavior: 'smooth' }); }
    catch { el.scrollTop = 0; }
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  const handleNavigate = (path) => {
    if (path === location.pathname || animating) return;
    setPrevPath(location.pathname);
    setHeaderRight(null);
    setAnimating(true);
    setTimeout(() => { setAnimating(false); setPrevPath(null); }, 400);
    navigate(path);
  };

  const IcoAdmin = ({s=20,c=T.text2}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
  const EXTRA_PAGES = { "/admin": { Icon: IcoAdmin, title: "Admin" } };
  const cur      = NAV.find(n => location.pathname === n.path || location.pathname.startsWith(n.path + "/")) || Object.entries(EXTRA_PAGES).find(([p]) => location.pathname === p || location.pathname.startsWith(p + "/"))?.[1];
  // Generate, Library and Admin render a tab row whose active underline is
  // meant to sit flush with the header's bottom border, so their header
  // content is bottom-aligned. Every other page (Bank, Settings, Dashboard,
  // etc.) just has icons / stats / account chrome, which read better
  // vertically centered in the header space instead of pinned to the edge.
  const headerBottomFlush = ["/generate","/library","/admin"].some(p => location.pathname === p || location.pathname.startsWith(p + "/"));
  const fromIdx  = NAV_PATHS.indexOf(prevPath || location.pathname);
  const toIdx    = NAV_PATHS.indexOf(location.pathname);
  const slideDir = toIdx >= fromIdx ? 1 : -1;
  const isGenerate = location.pathname === "/generate";

  const navButtons = NAV.map(n => {
    const active = location.pathname === n.path || location.pathname.startsWith(n.path + "/");
    // Unselected nav icons take the page-title turquoise (T.title) to match
    // the Video/Image generation tabs; the active page keeps the dark icon on
    // its yellow pill.
    const col    = active ? T.dark : T.title;
    const svg    = n.Icon({s:20, c:"currentColor", sw:45});
    const ico    = n.id === "generate" ? svg : React.cloneElement(svg, {strokeWidth:"1.5"});
    return (
      <button key={n.id} onClick={()=>handleNavigate(n.path)} title={n.title}
        style={{width:44,height:44,borderRadius:22,background:active?Y:"transparent",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s",color:col}}
        onMouseEnter={active?undefined:e=>{e.currentTarget.style.color=Y;}}
        onMouseLeave={active?undefined:e=>{e.currentTarget.style.color=T.title;}}>
        {ico}
      </button>
    );
  });

  const avatarBtn = (
    <div ref={menuRef} style={{position:"relative",flexShrink:0}}>
      <button onClick={()=>setMenuOpen(o=>!o)} title="Account"
        style={{width:32,height:32,borderRadius:"50%",background:Y,border:`3px solid ${Y}`,cursor:"pointer",overflow:"hidden",padding:0,display:"flex",alignItems:"center",justifyContent:"center",boxSizing:"border-box",position:"relative"}}>
        {userPhoto
          ? <img src={userPhoto} alt="Account" style={{width:"100%",height:"100%",objectFit:"cover"}} referrerPolicy="no-referrer"/>
          : <span style={{fontSize:13,fontWeight:900,color:T.dark}}>S</span>}
        {unreadReqs > 0 && <span style={{position:"absolute",top:-2,right:-2,width:10,height:10,borderRadius:"50%",background:T.danger,border:`2px solid ${T.dark}`}}/>}
      </button>
      {menuOpen && (
        <div style={{position:"absolute",...(narrow?{right:0,top:"calc(100% + 8px)"}:{left:"calc(100% + 8px)",bottom:0}),background:T.menuBg,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px 0",width:220,zIndex:200,boxShadow:"0 8px 32px rgba(0,0,0,0.5)"}}>
          <div style={{padding:"4px 16px 12px",borderBottom:`1px solid ${T.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              {userPhoto && <img src={userPhoto} alt="" style={{width:24,height:24,borderRadius:"50%",objectFit:"cover"}} referrerPolicy="no-referrer"/>}
              <span style={{color:T.text,fontSize:12,fontWeight:600}}>Signed in</span>
            </div>
            <span style={{color:T.text2,fontSize:11,wordBreak:"break-all"}}>{userEmail}</span>
          </div>
          {(userRole === "owner" || userRole === "admin") && (
            <button onClick={()=>{setMenuOpen(false);navigate("/admin");}} style={{width:"100%",padding:"10px 16px",background:"transparent",border:"none",color:T.text,fontSize:12,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:8,transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=T.overlay04} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text2} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Admin
              {unreadReqs > 0 && <span style={{width:8,height:8,borderRadius:"50%",background:T.danger,marginLeft:"auto"}}/>}
            </button>
          )}
          <button onClick={()=>{setMenuOpen(false);navigate("/settings/account");}} style={{width:"100%",padding:"10px 16px",background:"transparent",border:"none",color:T.text,fontSize:12,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:8,transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=T.overlay04} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text2} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Settings
          </button>
          <button onClick={()=>{
            // Toggle locally first (instant), then best-effort sync to
            // backend so cross-device theme stays consistent. The
            // matching toggle in Settings -> Appearance does the same.
            const next = themeMode === "dark" ? "light" : "dark";
            toggleTheme();
            api.saveAccountSettings({ theme: next }).catch(()=>{});
          }} style={{width:"100%",padding:"10px 16px",background:"transparent",border:"none",color:T.text,fontSize:12,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:8,transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=T.overlay04} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            {themeMode === "dark" ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text2} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text2} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            )}
            {themeMode === "dark" ? "Light theme" : "Dark theme"}
          </button>
          <button onClick={()=>{setMenuOpen(false);signOut(auth);}} style={{width:"100%",padding:"10px 16px",background:"transparent",border:"none",color:T.danger,fontSize:12,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:8,transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=T.overlay04} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );

  return (
    <HeaderRightCtx.Provider value={setHeaderRight}>
    <ProfileCtx.Provider value={refreshProfile}>
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"Mulish,sans-serif"}}>
      {/* Full-cover fixed background. Light mode applies a CSS filter to the
          same bg.jpg so the texture stays but the tones invert/desaturate
          into a pale teal-grey. */}
      <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,backgroundImage:`url(${BG_SRC})`,backgroundSize:"cover",backgroundPosition:"center",backgroundRepeat:"no-repeat",opacity:themeMode==="light"?0.25:0.2,filter:T.bgFilter,mixBlendMode:T.bgBlend}}/>
      {/* Subtle vignette overlay */}
      <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,background:`radial-gradient(ellipse at center, transparent 40%, ${T.vignette} 100%)`}}/>
      <div style={{position:"fixed",top:-300,left:"50%",transform:"translateX(-50%)",width:700,height:500,background:`radial-gradient(ellipse,${Y}06,transparent 70%)`,pointerEvents:"none",zIndex:0}}/>

      {/* ── Chrome: nav (top bar on narrow, sidebar on wide) ──────── */}
      {narrow ? (
        <nav style={{position:"fixed",top:0,left:0,right:0,height:56,background:T.chrome,borderBottom:`1px solid ${T.chromeBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 12px",zIndex:100,WebkitTransform:"translateZ(0)"}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{marginRight:4}}><Logo size={18}/></div>
            {navButtons}
          </div>
          {avatarBtn}
        </nav>
      ) : (
        <>
          <nav style={{position:"fixed",left:0,top:0,bottom:0,width:"4em",background:T.chrome,borderRight:`1px solid ${T.chromeBorder}`,display:"flex",flexDirection:"column",alignItems:"center",paddingTop:"1em",zIndex:100,WebkitTransform:"translateZ(0)"}}>
            <div style={{marginBottom:10}}><Logo size={28}/></div>
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,marginTop:0}}>
              {navButtons}
            </div>
          </nav>
          {/* Avatar pinned to viewport bottom-left, outside sidebar flow */}
          <div style={{position:"fixed",bottom:"1em",left:0,width:"4em",display:"flex",justifyContent:"center",zIndex:101,WebkitTransform:"translateZ(0)"}}>
            {avatarBtn}
          </div>
        </>
      )}

      {/* ── Page header (positioned differently for narrow vs wide, but same content).
            Inner row uses the same maxWidth:CONTENT_MAX as the content area
            below, so header chrome (page-title icon, notifications, the
            mode-tabs / preset-buttons block in GenerateView) aligns visually
            with the form / library / dashboard content under it. On narrow
            viewports the centering box collapses to full width. ── */}
      <div style={{position:"fixed",
        top:narrow?56:0,
        left:narrow?0:"4em",
        right:0,
        zIndex:50,
        // minHeight pins the header height so the page below doesn't
        // shift as header content changes. Narrow viewports use a
        // shorter header (the top navbar already takes 56px, so the
        // chrome doesn't need to eat as much vertical space).
        // Display flex-column + justifyContent flex-end pushes the
        // inner row to the bottom of that fixed-height box - that
        // combined with the inner row's own alignItems:flex-end gets
        // the tab underline flush with the header's bottom border. Pages
        // without a tab row center their content vertically instead.
        minHeight:narrow?60:84,
        display:"flex",
        flexDirection:"column",
        justifyContent:headerBottomFlush?"flex-end":"center",
        padding:narrow?"0 8px 0":"0 14px 0",
        borderBottom:`1px solid ${T.overlay04}`}}>
        <div style={{display:"flex",alignItems:headerBottomFlush?"flex-end":"center",gap:narrow?10:12,width:"100%",...(narrow?{}:{maxWidth:CONTENT_MAX,margin:"0 auto"})}}>
          {/* Generate and Library both have their own prominent tabs row
              in the header, so the page-title icon would be redundant +
              steal alignment space from those tabs. Hide it on those
              routes only; every other page keeps the icon as the
              visual anchor on the left. */}
          {cur && !location.pathname.startsWith("/generate") && !location.pathname.startsWith("/library") && <cur.Icon s={narrow?44:64} c={T.title}/>}
          {headerRight && <div style={{flex:1,display:"flex",alignItems:headerBottomFlush?"flex-end":"center",gap:6,justifyContent:"flex-end"}}>{headerRight}</div>}
        </div>
      </div>

      {/* ── Scrollable content area — Routes are rendered ONCE here so
            crossing the narrow/wide breakpoint doesn't unmount the route
            tree (which would lose all in-flight component state). ── */}
      <div ref={scrollerRef} style={{position:"fixed",
        top:narrow?116:84,
        left:narrow?0:"4em",
        right:0,bottom:0,overflowY:"auto",overflowX:"hidden",zIndex:1}}>
        <div key={"/" + location.pathname.split("/")[1]} style={{
          position:"relative",
          padding:narrow?"20px 12px 2em":"20px 1.75em 3em",
          animation:animating?`slideIn${slideDir>0?"Up":"Down"} 0.38s cubic-bezier(0.4,0,0.2,1) forwards`:"none",
        }}>
          <div style={{
            position:"relative",minHeight:"60vh",
            ...(narrow?{}:{maxWidth:CONTENT_MAX,margin:"0 auto"}),
          }}>
            {/* Fresh LoadingLockProvider keyed by route so each navigation
                starts with a clean pending set. AuthImage / AuthVideo and
                similar media components register locks here, and the route's
                PageLoader holds its reveal until pending hits 0. */}
            <LoadingLockProvider key={"lock-" + location.pathname.split("/")[1]}>
            <ScrollToTopCtx.Provider value={scrollToTop}>
            <Routes>
              <Route path="/generate/*" element={<GenerateView onDone={()=>handleNavigate("/library")}/>}/>
              <Route path="/library"   element={<LibraryView/>}/>
              <Route path="/library/:jobId" element={<LibraryView/>}/>
              <Route path="/dashboard" element={<DashboardView/>}/>
              <Route path="/bank"      element={<BankView/>}/>
              <Route path="/bank/purchase" element={<PurchaseView/>}/>
              <Route path="/settings/*"  element={<SettingsView/>}/>
              <Route path="/admin/*" element={(userRole === "owner" || userRole === "admin") ? <AdminView userRole={userRole}/> : <Navigate to="/generate" replace/>}/>
              <Route path="*"          element={<Navigate to="/generate" replace/>}/>
            </Routes>
            </ScrollToTopCtx.Provider>
            </LoadingLockProvider>
          </div>
        </div>
        {/* Global footer - shown at the bottom of every page. Centered
            column: two-color brand wordmark, then the community + guide
            links (Discord first, with the logos beside each guide), then
            the copyright line. */}
        <footer style={{padding:narrow?"44px 16px 48px":"56px 1.75em 60px"}}>
          <div style={{maxWidth:CONTENT_MAX,margin:"0 auto",display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",gap:26}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <img src="/logo-k.svg" alt="" style={{height:30,width:"auto",display:"block"}}/>
              <span style={{fontFamily:LABEL_FONT,fontSize:14,textTransform:"uppercase"}}>
                <span style={{color:Y,fontWeight:700,letterSpacing:1.5}}>Katana AI</span>{" "}
                {/* "Studio" matches the website footer wordmark: extralight
                    Rajdhani, turquoise, wide 0.2em tracking. */}
                <span style={{color:T.title,fontWeight:200,letterSpacing:"0.2em"}}>Studio</span>
              </span>
            </div>
            <nav style={{display:"flex",flexWrap:"wrap",justifyContent:"center",alignItems:"center",gap:narrow?"12px 18px":"10px 26px",fontFamily:"'Mulish', sans-serif",fontSize:12,fontWeight:600}}>
              <a href="https://discord.gg/zaKAuQkmdS" target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:7,color:T.text2,textDecoration:"none",transition:"color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.color=Y} onMouseLeave={e=>e.currentTarget.style.color=T.text2}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="#5865F2" aria-hidden="true"><path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.6 12.6 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.956 2.419-2.157 2.419zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.946 2.419-2.157 2.419z"/></svg>
                Join the Discord
              </a>
              <a href="https://cloud.google.com/blog/products/ai-machine-learning/ultimate-prompting-guide-for-veo-3-1" target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:7,color:T.text2,textDecoration:"none",transition:"color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.color=Y} onMouseLeave={e=>e.currentTarget.style.color=T.text2}>
                <img src="/gemini-logo.svg" alt="" style={{height:15,width:"auto",display:"block"}}/>
                Veo Prompting Guide
              </a>
              <a href="https://kling.ai/blog/kling-ai-prompt-guide" target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:7,color:T.text2,textDecoration:"none",transition:"color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.color=Y} onMouseLeave={e=>e.currentTarget.style.color=T.text2}>
                <img src="/kling-logo.png" alt="" style={{height:15,width:"auto",display:"block"}}/>
                Kling Prompting Guide
              </a>
              <a href="https://katana-ai.studio" target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:7,color:T.text2,textDecoration:"none",transition:"color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.color=Y} onMouseLeave={e=>e.currentTarget.style.color=T.text2}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                Website
              </a>
            </nav>
            <span style={{color:T.text3,fontSize:11}}>© {new Date().getFullYear()} Katana AI Studio</span>
          </div>
        </footer>
      </div>
    </div>
    </ProfileCtx.Provider>
    </HeaderRightCtx.Provider>
  );
}

// ── Root app with auth gate ───────────────────────────────────────────────────
export default function App() {
  // OAuth popup landing page. Hosted on the same origin as the rest of
  // the app to keep Discord's redirect URI off the bare *.run.app
  // domain (which trips Chrome Safe Browsing). The callback component
  // does its own work and never calls useAuth, so the popup window
  // doesn't run the auth gate or fetch settings.
  if (typeof window !== 'undefined' && window.location.pathname === '/auth/discord/callback') {
    return <DiscordCallback/>;
  }

  const { loggedIn, authChecked, signingIn, login, handleSignOut, userPhoto, userEmail, userRole, unreadReqs, setUnreadReqs, refreshProfile } = useAuth();

  // Dismiss the pre-React loading screen once auth is resolved
  useEffect(() => {
    if (!authChecked) return;
    const el = document.getElementById("pre-loader");
    if (el) {
      el.style.opacity = "0";
      setTimeout(() => el.remove(), 400);
    }
  }, [authChecked]);

  // Note: the dark-login override is driven directly by the auth-state
  // callback in useAuth (set on sign-out / token-reject, cleared once
  // authenticated) so it is applied before React re-renders the login
  // screen - a post-commit effect here would render it light first.

  if (!authChecked) return (
    <div style={{minHeight:"100vh",background:T.bg}}>
      <LeopardBg/>
    </div>
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="*" element={
          loggedIn
            ? <NotifyProvider><ConfirmProvider><Studio userPhoto={userPhoto} userEmail={userEmail} userRole={userRole} unreadReqs={unreadReqs} setUnreadReqs={setUnreadReqs} refreshProfile={refreshProfile}/></ConfirmProvider></NotifyProvider>
            : <LoginScreen onLogin={login} signingIn={signingIn}/>
        }/>
      </Routes>
    </BrowserRouter>
  );
}
